import { useContext, useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { ApiHelper as ToolkitApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import { HistoricalYieldTrendChart } from "@datanac/datanac-visualization-toolkit/dist/Components/rma-adm/HistoricalYieldTrendChart";
import { ProducerFarmCropWithHistoryChart } from "@datanac/datanac-visualization-toolkit/dist/Components/rma-aip-pass/ProducerFarmCropWithHistoryChart";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box, Divider, FormControl, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { _sessionManager, ApiHelper, UsersApiHelper, UsersApiHelperForMember } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { FarmVaultIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import _ from "lodash";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatPracticeName } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import MapViewer from "../../Reports/Vault/MapViewer";
import BaseAcresCard from "./components/BaseAcresCard";
import FieldCard from "./components/FieldCard";
import ProducerFarmBaseAcreDataTable from "./components/ProducerFarmBaseAcreDataTable";
import ProducerFarmCropWithHistoryDataGrid, {
  COLUMNS_PRODUCERFARMCROPWITHHISTORY,
} from "./components/ProducerFarmCropWithHistoryDataGrid";
import ProducerFarmFieldDataTable from "./components/ProducerFarmFieldDataTable";
import { CROP_YEAR_POTENTIAL } from "./helpers";
import { useFarmController } from "./useFarmController";

export default function FarmDetailWorkspace() {
  const { globalState } = useContext(AppContext);
  const { crop_year, location_state_name, location_county_name, producer_farm_id } = useParams();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const { component: confirmDialogComponent } = useDialog();
  const [isLoadingFarmDetails, setIsLoadingFarmDetails] = useState(true);
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [isLoadingBaseAcres, setIsLoadingBaseAcres] = useState(true);

  const [farmFilter] = useState({
    crop_year,
  });
  const { producerFarmFieldCrops } = useFarmController({
    ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
    filter: farmFilter,
    onError: handleError,
  });

  const [state, dispatch] = useReducer(propUpdateReducer, {
    is_active: true,
  });

  const loadProducerFarm = async () => {
    try {
      if (location_state_name && location_county_name && producer_farm_id !== "0") {
        const producerFilter = {
          producer_token: globalState.producer_token,
          ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
          id: producer_farm_id,
          is_active: true,
        };
        const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
        setIsLoadingFarmDetails(false);
        if (farms?.length) {
          dispatch({
            type: "updateMany",
            payload: farms[0],
          });
        }
      }
    } catch (err) {
      dispatch({
        type: "replace",
        payload: {},
      });
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token && producer_farm_id !== "0") {
      loadProducerFarm();
    } else {
      setIsLoadingFarmDetails(false);
    }
  }, [location_state_name, location_county_name, producer_farm_id, globalState?.producer_token]);

  // --- --- ---

  const FIELD_CARD_GRID_KEY = "field_workspace";
  const [fieldsWorkspaceMode, setFieldsWorkspaceMode] = useState(null);

  const { state: fields, actions: fieldActions } = useCardController([]);
  const loadProducerFarmFields = async () => {
    try {
      const fieldsFilter = {
        producer_token: globalState.producer_token,
        producer_farm_id: state?.id,
        ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
        is_active: true,
      };
      const fields = await UsersApiHelper.selectProducerFarmFields(fieldsFilter, globalState);
      fieldActions.replace(fields);

      setIsLoadingFields(false);
    } catch (err) {
      setIsLoadingFields(false);
      fieldActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (state?.id && state?.id !== "0" && globalState?.producer_token) {
      loadProducerFarmFields();
    }
  }, [state?.id, globalState?.producer_token]);

  // --- --- ---

  const BASE_ACRES_CARD_GRID_KEY = "base_acres_workspace";
  const [baseAcresWorkspaceMode, setBaseAcresWorkspaceMode] = useState(null);

  const { state: baseAcres, actions: baseAcreActions } = useCardController([]);
  const loadBaseAcres = async () => {
    try {
      const baseAcresFilter = {
        producer_token: globalState.producer_token,
        producer_farm_id: state?.id,
        ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
        is_active: true,
      };
      const baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      baseAcreActions.replace(baseAcres);

      setIsLoadingBaseAcres(false);
    } catch (err) {
      setIsLoadingBaseAcres(false);
      baseAcreActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (state?.id && state?.id !== "0" && globalState?.producer_token) {
      loadBaseAcres();
    }
  }, [state?.id, globalState?.producer_token]);

  // --- --- ---

  const { state: history, actions: historyActions } = useCardController([]);
  const loadProducerFarmCropWithHistory = async () => {
    try {
      const historyFilter = {
        agent_token: globalState.agent_token,

        producer_token: globalState.producer_token,

        location_state_name,
        location_county_name,
        fsa_farm_number: state?.fsa_farm_number,

        yield_type_code: "A",
      };

      let historyRaw = await UsersApiHelperForMember.callPost(
        "ProducerFarmFieldCropHistory/Search",
        historyFilter,
        globalState
      );
      if (!historyRaw?.length) {
        historyRaw = await ApiHelper.callPost("ProducerFarmCropWithHistory/Search", historyFilter, globalState);
      }
      const history = historyRaw.map((h, index) => ({
        id: index,
        ...h,
      }));

      history.forEach((h, index) => {
        h.id = index;
        h.yield_commodity_year = h.yield_commodity_year ?? h.yield_year;
        h.yield_acreage = h.yield_acreage ?? h.year_reported_acreage;
        h.unique_key = JSON.stringify(COLUMNS_PRODUCERFARMCROPWITHHISTORY.map((col) => h[col?.field]));
      });
      const _historyUnique = _.uniqBy(history, "unique_key");
      historyActions.replace(_historyUnique);
    } catch (err) {
      historyActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (
      globalState?.producer_token &&
      location_state_name &&
      location_county_name &&
      producer_farm_id &&
      state?.fsa_farm_number
    ) {
      loadProducerFarmCropWithHistory();
    }
  }, [
    location_state_name,
    location_county_name,
    producer_farm_id,
    state?.fsa_farm_number,
    globalState?.producer_token,
  ]);

  const { state: producerFarmField_union_Crops, actions: producerFarmField_union_CropActions } = useCardController();

  useEffect(() => {
    if (fields?.length && !producerFarmFieldCrops?.length) {
      producerFarmField_union_CropActions.replace(fields);
    } else if (fields?.length && (producerFarmFieldCrops?.length || producerFarmFieldCrops?.length)) {
      const loadUnionProducerFarmFieldCrops = async () => {
        // Union:
        const _producerFarmFieldsUnion = [...fields];
        _producerFarmFieldsUnion.forEach((currentProducerFarmField) => {
          currentProducerFarmField.ProducerFarmFieldCrops = producerFarmFieldCrops.filter(
            (crop) =>
              crop.producer_farm_id === currentProducerFarmField.producer_farm_id &&
              crop.producer_farm_field_id === currentProducerFarmField.id
          );

          currentProducerFarmField.CropProfiles = _.uniqBy(currentProducerFarmField?.ProducerFarmFieldCrops, (pfc) =>
            JSON.stringify({
              commodity_name: pfc.commodity_name,
              type_name: pfc.type_name,
              practice_name: pfc.practice_name,
            })
          )?.sort(
            (a, b) =>
              a.commodity_name?.localeCompare(b.commodity_name) ||
              a.type_name?.localeCompare(b.type_name) ||
              a.practice_name?.localeCompare(b.typpractice_namee_name)
          );
          currentProducerFarmField.CropProfiles_Sort = currentProducerFarmField.CropProfiles?.reduce(
            (accumulator, currentValue) =>
              accumulator + ", " + currentValue?.commodity_name + "-" + formatPracticeName(currentValue?.practice_name),
            ""
          );
        });

        producerFarmField_union_CropActions.replace(_producerFarmFieldsUnion);
      };
      loadUnionProducerFarmFieldCrops();
    } else {
      producerFarmField_union_CropActions.replace([]);
    }
  }, [globalState?.producer_token, fields, producerFarmFieldCrops]);

  // --- --- ---

  useEffect(() => {
    if (producer_farm_id === "0") {
      const _producer_farm_id = uuidv4();
      dispatch({
        type: "update",
        payload: { key: "id", value: _producer_farm_id },
      });
    } else {
      dispatch({
        type: "update",
        payload: { key: "id", value: producer_farm_id },
      });
    }
  }, [producer_farm_id]);

  useEffect(() => {
    dispatch({
      type: "updateMany",
      payload: {
        location_state_name,
        location_county_name,
      },
    });
  }, [location_state_name, location_county_name]);

  const isValid = () => {
    return state?.id && state?.fsa_farm_number && state?.farm_name;
  };
  useBlockerPrompt(!isValid(), "ProducerFarm");

  useEffect(() => {
    if (isValid() && globalState.producer_token && formStatusState?.isFormDirty) {
      formStatusActions.setIsFormDirty(true, true);

      UsersApiHelper.updateProducerFarm(state, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail, { preventDuplicate: true, variant: "error" });
        });
    }
  }, [state]);

  useEffect(() => {
    if (state.fsa_farm_number && (!state.farm_name || state.farm_name === "")) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: "farm_name", value: state.fsa_farm_number },
      });
    }
  }, [state]);

  // ---

  const [historyCropProfiles, setHistoryCropProfiles] = useState(null);
  const [historySelectedCropProfile, setHistorySelectedCropProfiles] = useState(null);
  const [selectedCountyPracticeHistory, setSelectedCountyPracticeHistory] = useState(null);

  const loadHistoryCropProfiles = async () => {
    const _historyCropProfiles = _.uniqBy(history, (pfc) =>
      JSON.stringify({
        commodity_name: pfc.commodity_name,
        type_name: pfc.type_name,
        practice_name: pfc.practice_name,
      })
    )
      ?.map((x) => ({
        reinsurance_year: getCurrentReinsuranceYear(),
        commodity_name: x.commodity_name,
        type_name: x.type_name,
        practice_name: x.practice_name,
        state_name: x.location_state_name,
        county_name: x.location_county_name,
      }))
      ?.sort(
        (a, b) =>
          a.commodity_name?.localeCompare(b.commodity_name) ||
          a.type_name?.localeCompare(b.type_name) ||
          a.practice_name?.localeCompare(b.practice_name)
      );
    setHistoryCropProfiles(_historyCropProfiles);
  };

  useEffect(() => {
    if (history?.length) {
      loadHistoryCropProfiles();
    }
  }, [history?.length]);

  useEffect(() => {
    if (historyCropProfiles?.length) {
      setHistorySelectedCropProfiles(historyCropProfiles[0]);
    }
  }, [historyCropProfiles?.length]);

  const handleChangeHistorySelectedCropProfileSelect = (event) => {
    setHistorySelectedCropProfiles(JSON.parse(event.target.value));
  };

  const loadSelectedCountyPracticeHistory = async () => {
    const _filteredHistory = history?.filter(
      (h) =>
        h.commodity_name === historySelectedCropProfile.commodity_name &&
        h.type_name === historySelectedCropProfile.type_name &&
        h.practice_name === historySelectedCropProfile.practice_name
    );
    if (_filteredHistory?.length) {
      setSelectedCountyPracticeHistory(_filteredHistory);
    } else {
      setSelectedCountyPracticeHistory(null);
    }
  };

  useEffect(() => {
    if (history?.length && historySelectedCropProfile) {
      loadSelectedCountyPracticeHistory();
    }
  }, [history?.length, historySelectedCropProfile]);

  // --- --- ---

  const handleChangeState = (event, value) => {
    if (!event?.target) return; // Prevent errors if event is undefined

    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    if (state[eventName] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: eventName, value: value },
      });
    }
  };

  const handleFieldOnChange = (currentField) => {
    if (currentField?.id) {
      formStatusActions.setIsFormDirty(true, true);

      UsersApiHelper.updateProducerFarmField(currentField, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
          loadProducerFarmFields();
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail, { preventDuplicate: true, variant: "error" });
        });
    }
  };

  const handleBaseAcresOnChange = (currentBaseAcres) => {
    if (currentBaseAcres?.id) {
      formStatusActions.setIsFormDirty(true, true);

      UsersApiHelper.updateProducerFarmBaseAcres(
        {
          ProducerFarmBaseAcres: [
            {
              ...currentBaseAcres,
              producer_token: globalState.producer_token,
              agent_token: globalState?.agent_token,
            },
          ],
        },
        globalState
      )
        .then(() => {
          formStatusActions.setIsSaving(false);
          loadBaseAcres();
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail, { preventDuplicate: true, variant: "error" });
        });
    }
  };

  const handleAddNewBaseAcresClick = () =>
    navigate(`/vault/${crop_year}/${location_state_name}/${location_county_name}/${state?.id}/baseAcre/0`);

  const handleAddNewFarmClick = () =>
    navigate(`/vault/${crop_year}/${location_state_name}/${location_county_name}/${state?.id}/0`);

  const [geojsonData, setGeojsonData] = useState(null);

  const handleMapUpdate = async (payload) => {
    try {
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null, // No caching
      });

      await api.callPost("ProducerFarmFieldMap", payload, globalState);
      loadGeojson();
    } catch (err) {
      console.error("Error saving GeoJSON:", err);
      handleError(err);
    }
  };

  const transformFeature = (feature) => ({
    features: [
      {
        id: feature.id,
        properties: {
          is_active: false,
          locstate: location_state_name,
          loccnty: location_county_name,
        },
        geometry: {
          type: feature.geometry.type,
          coordinates: feature.geometry.coordinates,
        },
      },
    ],
    producer_token: globalState.producer_token,
  });

  const handleMapDelete = async (geojson) => {
    const payload = transformFeature(geojson);
    handleMapUpdate(payload, "Map Deleted Successfully!");
  };

  const handleGeojsonChange = async (newGeojson) => {
    // Create a deep copy of newGeojson
    const payload = {
      ...newGeojson,
      features: newGeojson.features.map((feature) => ({
        ...feature,
        id: feature.id && !feature.id.includes("-") ? uuidv4() : feature.id,
        properties: {
          is_active: true,
          locstate: location_state_name,
          loccnty: location_county_name,
          farmnum: state?.fsa_farm_number,
        },
      })),
      producer_token: globalState.producer_token,
    };
    handleMapUpdate(payload);
  };

  const loadGeojson = async () => {
    try {
      const geojson = await UsersApiHelper.users_selectObject("ProducerFarmFieldMap", {
        producer_token: globalState.producer_token,
        location_state_name: location_state_name,
        location_county_name: location_county_name,
        fsa_farm_number: state?.fsa_farm_number,
        is_active: true,
      });
      let geoJsonData = {
        producer_token: globalState.producer_token,
        type: "FeatureCollection",
        features: [],
      };
      // If JSON is already in FeatureCollection format
      if (geojson?.type === "FeatureCollection" && Array.isArray(geojson.features)) {
        geoJsonData = geojson;
      }
      // If JSON is an array (Raw API Response)
      else if (Array.isArray(geojson)) {
        geoJsonData.features = geojson
          .map((feature) => {
            let geometry = feature.geometry;
            // Parse geometry if it's a string
            if (typeof geometry === "string") {
              try {
                geometry = JSON.parse(geometry);
              } catch {
                console.error("Invalid JSON geometry:", geometry);
                return null; //Skip this feature if JSON is invalid
              }
            }
            return {
              id: feature.id || uuidv4(),
              type: "Feature",
              geometry: {
                type: geometry?.type || "Polygon",
                coordinates: geometry?.coordinates || [],
              },
            };
          })
          .filter(Boolean); // Removes `null` values (invalid entries)
      }
      // Update state only if valid data is received
      if (geoJsonData.features.length > 0) {
        setGeojsonData(geoJsonData);
      } else {
        setGeojsonData(null);
      }
    } catch (err) {
      console.error("Error fetching GeoJSON:", err);
      handleError(err);
    }
  };
  const reloadParent = () => {
    loadGeojson();
  };
  useEffect(() => {
    if (state?.fsa_farm_number) {
      loadGeojson();
    }
  }, [state?.fsa_farm_number]);

  return (
    <>
      <WorkspaceHeader
        title={state?.farm_name || state?.fsa_farm_number || "Farm"}
        category={{
          title: "Farm Vault",
          icon: <FarmVaultIcon />,
        }}
        breadCrumbs={[
          {
            title:
              (crop_year !== CROP_YEAR_POTENTIAL ? "Planted Acres" : "Farm Potential") +
              ((crop_year !== CROP_YEAR_POTENTIAL && " - " + crop_year) || ""),
            path: "/vault" + (crop_year === CROP_YEAR_POTENTIAL ? "/potential" : ""),
          },
          {
            title: location_county_name + ", " + location_state_name,
            path: `/vault/${crop_year}/${location_state_name}/${location_county_name}`,
          },
        ]}
      />

      <WorkspacePageHeader title="Farm Details" additionalControls={formStatusComponent} />
      <Grid container spacing={1}>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl fullWidth={true}>
            <StatefulControlledTextField
              isLoading={isLoadingFarmDetails}
              label="Farm Name"
              name="farm_name"
              isRequired={true}
              defaultValue={state.farm_name || ""}
              variant="outlined"
              onChange={handleChangeState}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl fullWidth={true}>
            <StatefulControlledTextField
              isLoading={isLoadingFarmDetails}
              label="FSN #"
              name="fsa_farm_number"
              isRequired={true}
              defaultValue={state.fsa_farm_number || ""}
              variant="outlined"
              onChange={handleChangeState}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl fullWidth={true}>
            <StatefulControlledTextField
              isLoading={isLoadingFarmDetails}
              label="Other Shares"
              name="other_person_sharing"
              isRequired={false}
              defaultValue={state.other_person_sharing || ""}
              variant="outlined"
              onChange={handleChangeState}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Divider />

      <WorkspacePageHeader
        title="Fields"
        viewMode={fieldsWorkspaceMode}
        onViewModeChange={setFieldsWorkspaceMode}
        cardGridKey={FIELD_CARD_GRID_KEY}
        isLoading={isLoadingFields}
      />
      {fieldsWorkspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {producerFarmField_union_Crops.map((currentProducerFarmField, index) => (
            <FieldCard
              key={index}
              sx={{ cursor: crop_year === CROP_YEAR_POTENTIAL ? "default" : "pointer" }}
              producerFarm={state}
              producerFarmField={currentProducerFarmField}
              onChange={handleFieldOnChange}
            />
          ))}
          {!fields?.length && (
            <EmptyCard
              text="No fields listed"
              secondaryText="Get started by adding a field"
              isLoading={isLoadingFields}
            />
          )}
        </Box>
      )}
      {fieldsWorkspaceMode === WORKSPACE_VIEW_TABLE && (
        <ProducerFarmFieldDataTable
          producerFarm={state}
          producerFarmFields={producerFarmField_union_Crops}
          isLoading={isLoadingFields}
          onChange={handleFieldOnChange}
        />
      )}

      <Divider />

      <WorkspacePageHeader
        title="Base Acres"
        viewMode={baseAcresWorkspaceMode}
        onViewModeChange={setBaseAcresWorkspaceMode}
        cardGridKey={BASE_ACRES_CARD_GRID_KEY}
        isLoading={isLoadingBaseAcres}
      />
      {baseAcresWorkspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {baseAcres.map((currentBaseAcre, index) => (
            <BaseAcresCard
              key={index}
              producerFarm={state}
              baseAcres={currentBaseAcre}
              onChange={handleBaseAcresOnChange}
            />
          ))}
          {!baseAcres?.length && (
            <EmptyCard
              text="No base acres listed"
              secondaryText="Get started by adding base acres"
              isLoading={isLoadingBaseAcres}
            />
          )}
        </Box>
      )}
      {baseAcresWorkspaceMode === WORKSPACE_VIEW_TABLE && (
        <ProducerFarmBaseAcreDataTable
          producerFarm={state}
          producerFarmBaseAcres={baseAcres}
          isLoading={isLoadingBaseAcres}
          onChange={handleBaseAcresOnChange}
        />
      )}

      {Boolean(history?.length) && (
        <>
          <Divider />
          <WorkspacePageHeader
            title="Farm History"
            additionalControls={
              historyCropProfiles?.length && historySelectedCropProfile ? (
                <Select
                  labelId="historySelectedCropProfileLabel"
                  value={JSON.stringify(historySelectedCropProfile)}
                  name={"historySelectedCropProfile"}
                  onChange={handleChangeHistorySelectedCropProfileSelect}
                  label={"Crop"}
                >
                  {historyCropProfiles?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={JSON.stringify(currentItem)}>
                        {currentItem.commodity_name + " "}/{" "}
                        {currentItem.type_name === "No Type Specified" ? "" : currentItem.type_name + " / "}
                        {currentItem.practice_name}
                      </MenuItem>
                    );
                  })}
                </Select>
              ) : null
            }
          />
          <ProducerFarmCropWithHistoryDataGrid producerFarmCropWithHistory={selectedCountyPracticeHistory || []} />
        </>
      )}

      {historySelectedCropProfile && (
        <>
          <Typography variant="h1">Farm / County Comparison</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <HistoricalYieldTrendChart
                countyPractice={{
                  reinsurance_year: crop_year !== CROP_YEAR_POTENTIAL ? crop_year : getCurrentReinsuranceYear(),
                  commodity_name: historySelectedCropProfile?.commodity_name,
                  type_name: historySelectedCropProfile?.type_name,
                  practice_name: historySelectedCropProfile?.practice_name,
                  state_name: location_state_name,
                  county_name: location_county_name,
                }}
                yield_year_minimum={null}
                yield_year_maximum={null}
                api={ApiHelper}
                size={null}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProducerFarmCropWithHistoryChart
                countyPractice={{
                  reinsurance_year: crop_year !== CROP_YEAR_POTENTIAL ? crop_year : getCurrentReinsuranceYear(),
                  commodity_name: historySelectedCropProfile?.commodity_name,
                  type_name: historySelectedCropProfile?.type_name,
                  practice_name: historySelectedCropProfile?.practice_name,
                  state_name: location_state_name,
                  county_name: location_county_name,
                }}
                producerFarmCropWithHistory={selectedCountyPracticeHistory}
                yield_year_minimum={null}
                yield_year_maximum={null}
                api={ApiHelper}
                size={null}
              />
            </Grid>
          </Grid>
        </>
      )}

      <WorkspaceActionBar
        content={
          <>
            <Button
              variant="contained"
              onClick={handleAddNewBaseAcresClick}
              disabled={!isValid() || crop_year === CROP_YEAR_POTENTIAL}
            >
              <AddCircleOutline sx={{ mr: 1 }} />
              Add Base Acres
            </Button>
            <Button
              variant="contained"
              onClick={handleAddNewFarmClick}
              disabled={!isValid() || crop_year === CROP_YEAR_POTENTIAL}
            >
              <AddCircleOutline sx={{ mr: 1 }} />
              New Field
            </Button>
          </>
        }
      />
      <Divider />
      <div className="showMap">
        <MapViewer
          onMapDelete={handleMapDelete}
          onReload={reloadParent}
          geojson={geojsonData}
          onChange={handleGeojsonChange}
        />
      </div>

      {confirmDialogComponent}
    </>
  );
}
